import { useState, useEffect, useRef } from "react";
import { casualData } from "../api/casualData.js";
import { COUNTRY_CODES } from "../lib/countryCodes.js";

const PAYMENT_METHODS = [
  { value: "", label: "Nepasirinkta" },
  { value: "cash", label: "Grynieji" },
  { value: "bank_transfer", label: "Bankinis pavedimas" },
  { value: "card", label: "Mokėjimo kortelė" },
];

const EMPTY_PROFILE = {
  firstName: "", lastName: "", address: "", deliveryAddress: "", postalCode: "",
  phoneCode: "+370", phoneCountry: "Lietuva", phone: "", email: "", paymentMethod: "",
};

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm";
const lbl = "block text-xs font-semibold text-gray-600 mb-1";

// Apibrėžta už komponento ribų — jei būtų viduje, React laikytų ją nauju
// komponento tipu kiekviename render'e ir perkurtų <input> DOM elementą po
// kiekvieno klavišo paspaudimo (dėl to fokusas dingdavo įvedus vieną raidę).
function ProfileField({ label, field, editing, form, setForm, type, placeholder }) {
  return (
    <div className="mb-3">
      <label className={lbl}>{label}</label>
      {editing
        ? <input type={type || "text"} className={inp} value={form[field] || ""} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} placeholder={placeholder || ""} />
        : <div className="px-3 py-2 text-sm border border-transparent rounded-lg bg-gray-50 text-gray-800">{form[field] || <span className="text-gray-300">—</span>}</div>
      }
    </div>
  );
}

// Pasirinktas kodas suskleidus rodo tik kodą (pvz. "+370"), o valstybė matoma
// tik išskleidus sąrašą — natyvus <select> to negali (visada rodo pilną
// pasirinkto <option> tekstą), todėl čia savas išskleidžiamas sąrašas.
function PhoneCodeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="border border-gray-200 rounded-lg text-sm"
        style={{ padding: "8px 10px", background: "#fff", cursor: "pointer", minWidth: 68, textAlign: "left" }}>
        {value} ▾
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", maxHeight: 240, overflowY: "auto", zIndex: 20, minWidth: 210 }}>
          {COUNTRY_CODES.map(c => (
            <div key={c.code + c.country}
              onClick={() => { onChange(c); setOpen(false); }}
              style={{ padding: "7px 12px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", display: "flex", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ fontWeight: 600, minWidth: 44 }}>{c.code}</span>
              <span style={{ color: "#6b7280" }}>{c.country}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CasualProfileView({ user }) {
  const [profilis, setProfilis] = useState(EMPTY_PROFILE);
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    casualData.getProfile()
      .then(data => {
        if (cancelled) return;
        const merged = { ...EMPTY_PROFILE, ...data };
        setProfilis(merged);
        setForm(merged);
      })
      .catch(e => console.error("Failed to load casual profile", e))
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  async function save() {
    setSaving(true);
    try {
      await casualData.putProfile(form);
      setProfilis(form);
      setEditing(false);
    } catch (e) {
      console.error("Failed to save casual profile", e);
      alert("Nepavyko išsaugoti profilio. Bandykite dar kartą.");
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return <p style={{ fontSize: 14, color: "#383853" }}>Kraunama...</p>;
  }

  const fp = { editing, form, setForm };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">👤 Mano profilis</h2>
          <p className="text-sm text-gray-500 mt-1">Jūsų kontaktinė informacija ir atsiskaitymo nustatymai</p>
        </div>
        {!editing
          ? <button onClick={() => setEditing(true)} className="px-4 py-2 rounded-lg bg-ral3000 text-white text-sm font-semibold">✏️ Redaguoti</button>
          : <div className="flex gap-2">
              <button disabled={saving} onClick={save} className="px-4 py-2 rounded-lg bg-ral7012 text-white text-sm font-semibold disabled:opacity-60">{saving ? "Saugoma..." : "💾 Išsaugoti"}</button>
              <button onClick={() => { setForm(profilis); setEditing(false); }} className="px-4 py-2 rounded-lg bg-stone-100 text-gray-600 text-sm font-semibold">Atšaukti</button>
            </div>
        }
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ProfileField {...fp} label="Vardas" field="firstName" placeholder="Vardas" />
        <ProfileField {...fp} label="Pavardė" field="lastName" placeholder="Pavardė" />
        <ProfileField {...fp} label="Adresas" field="address" placeholder="Gatvė, miestas" />
        <ProfileField {...fp} label="Pristatymo adresas" field="deliveryAddress" placeholder="Jei skiriasi nuo adreso" />
        <ProfileField {...fp} label="Pašto kodas" field="postalCode" placeholder="LT-00000" />
        <ProfileField {...fp} label="El. paštas" field="email" type="email" placeholder="vardas@pavyzdys.lt" />

        {/* Telefono numeris: valstybės kodo pasirinkimas + numeris */}
        <div className="mb-3">
          <label className={lbl}>Kontaktinis telefono numeris</label>
          {editing ? (
            <div className="flex gap-2">
              <PhoneCodeSelect
                value={form.phoneCode || "+370"}
                onChange={c => setForm(p => ({ ...p, phoneCode: c.code, phoneCountry: c.country }))}
              />
              <input className={inp} value={form.phone || ""} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="600 00000" />
            </div>
          ) : (
            <div className="px-3 py-2 text-sm border border-transparent rounded-lg bg-gray-50 text-gray-800">
              {form.phone ? `${form.phoneCode || ""} ${form.phone}` : <span className="text-gray-300">—</span>}
            </div>
          )}
        </div>

        {/* Atsiskaitymų nustatymai */}
        <div className="mb-3">
          <label className={lbl}>Atsiskaitymų nustatymai</label>
          {editing ? (
            <select className={inp} value={form.paymentMethod || ""} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          ) : (
            <div className="px-3 py-2 text-sm border border-transparent rounded-lg bg-gray-50 text-gray-800">
              {form.paymentMethod
                ? PAYMENT_METHODS.find(m => m.value === form.paymentMethod)?.label
                : <span className="text-gray-300">—</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
